// Thin wrapper around pdf.js for the Drawings viewer. The worker is bundled by
// Vite via `?worker` — it emits a plain `.js` worker chunk and instantiates it
// through Vite's own worker plumbing, so it works regardless of how the host
// serves `.mjs` files (pointing workerSrc at a `.mjs` URL fails in production
// when the server doesn't send a JS MIME type for `.mjs`).
import * as pdfjsLib from "pdfjs-dist";
import PdfjsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfjsWorker();

export type PdfDoc = pdfjsLib.PDFDocumentProxy;

/** Number of pages in a PDF File (used to record page_count on upload). */
export async function countPdfPages(file: File): Promise<number> {
  const data = await file.arrayBuffer();
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const n = doc.numPages;
  await doc.destroy();
  return n;
}

/** Load a PDF document from a (presigned) URL. Caller must destroy() it. */
export function loadPdf(url: string): Promise<PdfDoc> {
  return pdfjsLib.getDocument({ url }).promise;
}

/** Render one page to a fresh canvas at the given target CSS width. */
export async function renderPdfPage(
  doc: PdfDoc,
  pageNumber: number,
  targetWidth: number,
): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  const page = await doc.getPage(pageNumber);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.max(0.2, targetWidth / base.width);
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.floor(viewport.width);
  canvas.height = Math.floor(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas unavailable");
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, width: canvas.width, height: canvas.height };
}
